import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useLoadScript, MarkerF } from '@react-google-maps/api';
import ConversionPopup from './ConversionPopup';
import EmptyAreaPopup from './EmptyAreaPopup';
import DemoReviewModal from './DemoReviewModal';
import DemoTooltip from './DemoTooltip';
import { Menu, X, MapPin } from 'lucide-react';
import { MdLogin } from 'react-icons/md';
import { FaShareSquare, FaSlideshare } from 'react-icons/fa';
import { IoShareSocialOutline } from 'react-icons/io5';
import { IoShareSocial } from 'react-icons/io5';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeApp, getApps } from 'firebase/app';
import './DemoMap.css';

// Default center coordinates (will be overridden by user location)
const DEFAULT_CENTER = { lat: 39.0997, lng: -94.5786 };
const DEFAULT_ZOOM = 12;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DATABASE_ID_HASH = process.env.REACT_APP_DATABASE_ID_HASH;

// Debugging logs for Environment Variables
console.log('[DemoMap] Initializing with Environment Variables:');
console.log('[DemoMap] Google Maps Key exists:', !!GOOGLE_MAPS_API_KEY);
console.log('[DemoMap] Database ID Hash exists:', !!DATABASE_ID_HASH);

if (!GOOGLE_MAPS_API_KEY) {
  console.error('[DemoMap] REACT_APP_GOOGLE_MAPS_API_KEY is UNDEFINED. Check your .env file and restart the server.');
}
if (!DATABASE_ID_HASH) {
  console.error('[DemoMap] REACT_APP_DATABASE_ID_HASH is UNDEFINED. Check your .env file and restart the server.');
}

// Sample realistic review data for demo - organized by rating
const SAMPLE_REVIEWS = {
  5: [
    {
      id: "review-5-1",
      rating: 5.0,
      comment: "Absolutely pristine facilities! Spotless, well-stocked, and beautifully maintained. The staff clearly takes pride in keeping this place perfect. Cleanest public restroom I've ever used - would go out of my way just to visit again!",
      username: "CleanFreak2024",
      createdAt: "2024-01-15T10:30:00.000Z",
      hasBidet: true,
      hasChangingTable: true,
      hasFeminineProducts: true,
      hasAirDryer: true,
      hasHandicapAccessible: true,
      cleanlinessRating: 5,
      accessibilityRating: 5
    },
    {
      id: "review-5-2",
      rating: 5.0,
      comment: "Five stars doesn't seem enough! This place is immaculate with premium amenities. Everything from the automatic faucets to the high-end hand dryers works perfectly. The attention to detail is incredible - even the floors sparkle!",
      username: "LuxuryLover88",
      createdAt: "2024-01-12T14:20:00.000Z",
      hasBidet: true,
      hasChangingTable: true,
      hasFeminineProducts: true,
      hasAirDryer: true,
      hasHandicapAccessible: true,
      cleanlinessRating: 5,
      accessibilityRating: 5
    }
  ],
  4: [
    {
      id: "review-4-1",
      rating: 4.2,
      comment: "Great overall experience! Very clean and well-maintained facilities. All the essentials are available and everything works properly. Minor wear and tear but nothing concerning. Would definitely recommend!",
      username: "PracticalReviewer",
      createdAt: "2024-01-10T16:45:00.000Z",
      hasBidet: false,
      hasChangingTable: true,
      hasFeminineProducts: true,
      hasAirDryer: true,
      hasHandicapAccessible: true,
      cleanlinessRating: 4,
      accessibilityRating: 4
    },
    {
      id: "review-4-2",
      rating: 4.5,
      comment: "Solid choice! Clean, functional, and well-stocked. The facilities are in good condition and everything works as expected. Staff seems to keep up with maintenance regularly. No complaints here!",
      username: "AverageJoe23",
      createdAt: "2024-01-08T11:30:00.000Z",
      hasBidet: true,
      hasChangingTable: true,
      hasFeminineProducts: false,
      hasAirDryer: true,
      hasHandicapAccessible: true,
      cleanlinessRating: 4,
      accessibilityRating: 5
    }
  ],
  3: [
    {
      id: "review-3-1",
      rating: 3.0,
      comment: "It's alright - gets the job done. Facilities are functional but showing some age. Basic amenities available, though some things might be out of stock. Could use a deep clean but not terrible in a pinch.",
      username: "JustOkay99",
      createdAt: "2024-01-14T09:15:00.000Z",
      hasBidet: false,
      hasChangingTable: false,
      hasFeminineProducts: true,
      hasAirDryer: false,
      hasHandicapAccessible: true,
      cleanlinessRating: 3,
      accessibilityRating: 3
    },
    {
      id: "review-3-2",
      rating: 3.2,
      comment: "Average facilities. Nothing impressive but nothing terrible either. Restrooms are usable but could use some maintenance attention. Paper products sometimes run low during busy periods.",
      username: "MiddleOfTheRoad",
      createdAt: "2024-01-11T13:20:00.000Z",
      hasBidet: false,
      hasChangingTable: true,
      hasFeminineProducts: false,
      hasAirDryer: false,
      hasHandicapAccessible: true,
      cleanlinessRating: 3,
      accessibilityRating: 4
    }
  ],
  2: [
    {
      id: "review-2-1",
      rating: 2.0,
      comment: "Below average experience. Facilities are dated and not very clean. Some amenities were missing or broken. Floor was sticky and trash cans were overflowing. Only use if absolutely necessary.",
      username: "DisappointedUser",
      createdAt: "2024-01-13T15:30:00.000Z",
      hasBidet: false,
      hasChangingTable: false,
      hasFeminineProducts: false,
      hasAirDryer: false,
      hasHandicapAccessible: false,
      cleanlinessRating: 2,
      accessibilityRating: 2
    },
    {
      id: "review-2-2",
      rating: 2.3,
      comment: "Needs serious improvement. Restrooms show clear signs of neglect - dirty surfaces, empty soap dispensers, and unpleasant odors. Basic functionality is there but the experience is unpleasant.",
      username: "CriticalReviewer",
      createdAt: "2024-01-09T17:45:00.000Z",
      hasBidet: false,
      hasChangingTable: false,
      hasFeminineProducts: false,
      hasAirDryer: false,
      hasHandicapAccessible: true,
      cleanlinessRating: 2,
      accessibilityRating: 3
    }
  ],
  1: [
    {
      id: "review-1-1",
      rating: 1.0,
      comment: "Absolutely terrible experience. Filthy conditions, no toilet paper, broken facilities, and overwhelming odor. This place should be avoided at all costs. I'd rather find a bush than use this restroom again.",
      username: "WarningEveryone",
      createdAt: "2024-01-16T12:00:00.000Z",
      hasBidet: false,
      hasChangingTable: false,
      hasFeminineProducts: false,
      hasAirDryer: false,
      hasHandicapAccessible: false,
      cleanlinessRating: 1,
      accessibilityRating: 1
    },
    {
      id: "review-1-2",
      rating: 1.5,
      comment: "Worst public restroom I've ever encountered. Disgusting conditions with no maintenance whatsoever. Multiple facilities were broken, trash everywhere, and clearly hasn't been cleaned in days. Health hazard!",
      username: "NeverAgain99",
      createdAt: "2024-01-07T14:30:00.000Z",
      hasBidet: false,
      hasChangingTable: false,
      hasFeminineProducts: false,
      hasAirDryer: false,
      hasHandicapAccessible: false,
      cleanlinessRating: 1,
      accessibilityRating: 2
    }
  ]
};

// Helper function to safely format rating
const formatRating = (rating) => {
  const safeRating = typeof rating === 'number' ? rating : 3;
  return safeRating.toFixed(1);
};

function DemoMap({ onReviewView }) {
  // Remounting the content re-runs useLoadScript, which is the only way
  // to retry a failed Maps script load.
  const [retryKey, setRetryKey] = useState(0);
  return (
    <DemoMapContent
      key={retryKey}
      onReviewView={onReviewView}
      onRetry={() => setRetryKey((k) => k + 1)}
    />
  );
}

function DemoMapContent({ onReviewView, onRetry }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    if (isLoaded) {
      console.log('[DemoMap] Google Maps Script Loaded Successfully');
    }
    if (loadError) {
      console.error('[DemoMap] Google Maps Load Error:', loadError);
    }
  }, [isLoaded, loadError]);

  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [demoRestrooms, setDemoRestrooms] = useState([]);
  const [hasViewedReview, setHasViewedReview] = useState(false);
  const [showEmptyAreaPopup, setShowEmptyAreaPopup] = useState(false);
  const [emptyAreaPosition, setEmptyAreaPosition] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRestroom, setSelectedRestroom] = useState(null);
  const mapRef = useRef(null);
  const debounceTimer = useRef(null);

  // Custom rating marker icon - dynamic based on rating
  const getPoopIcon = useCallback((rating) => {
    if (!window.google) return null;
    
    const averageRating = rating || 3;
    let iconNumber;
    if (averageRating <= 1.9) iconNumber = 1;
    else if (averageRating <= 2.9) iconNumber = 2;
    else if (averageRating <= 3.9) iconNumber = 3;
    else if (averageRating <= 4.79) iconNumber = 4;
    else iconNumber = 5;
    
    return {
      url: `${process.env.PUBLIC_URL}/assets/ratings/TCM-RatingDisplay-${iconNumber}.png`,
      scaledSize: new window.google.maps.Size(35, 35),
      anchor: new window.google.maps.Point(17, 17),
    };
  }, []);

  // Fetch demo restrooms from cloud function
  useEffect(() => {
    const fetchDemoRestrooms = async () => {
      try {
        const firebaseConfig = {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID
        };
        
        // NOTE: the default Firebase app is already initialized by the time
        // the demo map mounts, so a bare initializeApp() would throw
        // "duplicate-app" and leave demoRestrooms empty forever (no markers,
        // no marker clicks, no empty-area popup). A named secondary instance
        // never collides, and it intentionally bypasses any local functions
        // emulator so demo reads always hit production functions.
        const app = getApps().find((a) => a.name === 'demo-reads')
          ?? initializeApp(firebaseConfig, 'demo-reads');
        const functions = getFunctions(app);
        const getRestroomSummary = httpsCallable(functions, 'GetRestroomSummary');
        
        // Pass database_id_hash to the cloud function
        const result = await getRestroomSummary({
          database_id_hash: DATABASE_ID_HASH || ''
        });

        const restrooms = result.data.restrooms || [];
        
        const transformedRestrooms = restrooms.map(restroom => ({
          id: restroom.geohash,
          name: restroom.name,
          latitude: restroom.latitude,
          longitude: restroom.longitude,
          rating: restroom.averageRating || restroom.rating,
          category: restroom.category,
          verified: restroom.verified,
          reviews: []
        }));
        
        setDemoRestrooms(transformedRestrooms);
        setLoading(false);
      } catch (error) {
        console.error('[DemoMap] Error fetching demo restrooms:', error);
        setLoading(false);
      }
    };

    fetchDemoRestrooms();
  }, []);

  // Get user location and set map center
  useEffect(() => {
    const getUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const pos = { lat: latitude, lng: longitude };
            console.log('[DemoMap] User location found:', pos);
            setUserLocation(pos);
            setMapCenter(pos);
            setLocationLoading(false);
          },
          (error) => {
            console.warn('[DemoMap] Geolocation error:', error.message);
            setLocationLoading(false);
          },
          {
            timeout: 10000,
            enableHighAccuracy: true
          }
        );
      } else {
        console.warn('[DemoMap] Geolocation not supported');
        setLocationLoading(false);
      }
    };

    getUserLocation();
  }, []);

  const handleMarkerClick = (restroom) => {
    console.log('[DemoMap] Marker clicked:', restroom.name);
    if (!hasViewedReview) {
      setSelectedRestroom(restroom);
      setShowReviewModal(true);
      setHasViewedReview(true);
    } else {
      if (onReviewView) {
        onReviewView();
      }
    }
  };

  const handleInviteFriends = () => {
    const inviteText = "Check out The Crapper Mapper - the #1 app for your #2 needs! 🚽✨";
    const inviteUrl = window.location.origin;
    
    if (navigator.share) {
      navigator.share({
        title: 'The Crapper Mapper',
        text: inviteText,
        url: inviteUrl
      }).catch(err => {});
    } else {
      navigator.clipboard.writeText(`${inviteText} ${inviteUrl}`).then(() => {
        alert('Invite link copied to clipboard! 📋');
      }).catch(err => {});
    }
    
    setShowHamburgerMenu(false);
  };

  // Check if current map area has few or no restrooms
  const checkForEmptyArea = useCallback(() => {
    if (!mapRef.current || demoRestrooms.length === 0) return;

    const bounds = mapRef.current.getBounds();
    if (!bounds) return;
    
    const visibleRestrooms = demoRestrooms.filter(restroom => {
      return bounds.contains({ lat: restroom.latitude, lng: restroom.longitude });
    });

    console.log('[DemoMap] Visible restrooms count:', visibleRestrooms.length);

    if (visibleRestrooms.length < 2) {
      const center = mapRef.current.getCenter();
      setEmptyAreaPosition({ lat: center.lat(), lng: center.lng() });
      setShowEmptyAreaPopup(true);
    } else {
      setShowEmptyAreaPopup(false);
    }
  }, [demoRestrooms]);

  const onMapIdle = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      checkForEmptyArea();
    }, 1500);
  };

  const onMapLoad = useCallback((map) => {
    console.log('[DemoMap] Map Loaded');
    mapRef.current = map;
  }, []);

  // Close review modal
  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedRestroom(null);
  };

  // Close empty area popup
  const closeEmptyAreaPopup = () => {
    setShowEmptyAreaPopup(false);
    setEmptyAreaPosition(null);
  };

  if (loadError) return (
    <div style={{
      height: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f1ec',
      padding: '20px',
    }}>
      <div style={{
        maxWidth: '420px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '32px 24px',
        textAlign: 'center',
        boxShadow: '0 4px 16px rgba(97, 79, 72, 0.15)',
      }}>
        <div style={{ fontSize: '2.5rem' }} aria-hidden="true">🗺️</div>
        <h2 style={{ color: '#231F20', margin: '12px 0 8px' }}>The map couldn&apos;t load</h2>
        <p style={{ color: '#666', margin: '0 0 16px' }}>
          Check your connection and try again — your restrooms are waiting.
        </p>
        <button className="btn btn-secondary" onClick={onRetry}>
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ height: '100vh', width: '100%', position: 'relative' }}>
      {/* Demo Map Header */}
      <div className="demo-header">
        <div className="demo-header-content">
          <div className="demo-header-text">
            <img src="app-icon.png" alt="The Crapper Mapper" className="demo-header-icon" />
            <div>
              <h1>The Crapper Mapper</h1>
              <p>The #1 App for your #2 Needs </p>
            </div>
          </div>
        </div>
        <div className="demo-header-actions">
          <button 
            className="demo-hamburger-btn"
            onClick={() => setShowHamburgerMenu(!showHamburgerMenu)}
            aria-label={showHamburgerMenu ? "Close menu" : "Open menu"}
            aria-expanded={showHamburgerMenu}
          >
            {showHamburgerMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Hamburger Menu Dropdown */}
        {showHamburgerMenu && (
          <div className="demo-hamburger-menu">
            <button 
              className="demo-menu-item" 
              onClick={() => window.location.href = '/auth?view=login'}
            >
              <MdLogin style={{ color: '#231F20', marginRight: '8px' }} />
              Create Account / Login
            </button>
            <button 
              className="demo-menu-item" 
              onClick={handleInviteFriends}
            >
              <FaShareSquare style={{ color: '#231F20', marginRight: '8px' }} />
              Invite Friends
            </button>
            <button 
              className="demo-menu-item" 
              onClick={() => window.location.href = '/socials'}
            >
              <IoShareSocial style={{ color: '#231F20', marginRight: '8px' }} />
              Community
            </button>
            <button 
              className="demo-menu-item" 
              onClick={() => window.location.href = '/ambassador'}
            >
              <FaSlideshare style={{ color: '#231F20', marginRight: '8px' }} />
              Join Ambassador Team
            </button>
          </div>
        )}
      </div>

      {/* Map Container */}
      <div style={{ height: '100%', paddingTop: '80px' }}>
        {(!isLoaded || loading || locationLoading) ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            background: '#f5f5f5'
          }}>
            <img
              src="/app-icon.png"
              alt="The Crapper Mapper"
              style={{ width: '72px', height: '72px', objectFit: 'contain', marginBottom: '16px' }}
            />
            <div style={{ fontSize: '18px', color: '#666' }}>
              {!isLoaded ? 'Loading Maps...' : (locationLoading ? 'Finding your location...' : 'Loading restrooms...')}
            </div>
            {locationLoading && (
              <div style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>
                (Helping us show restrooms near you!)
              </div>
            )}
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={{ height: '100%', width: '100%' }}
            center={mapCenter}
            zoom={userLocation ? 14 : DEFAULT_ZOOM}
            onLoad={onMapLoad}
            onIdle={onMapIdle}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false
            }}
          >
            {demoRestrooms.map((restroom) => (
              <MarkerF
                key={`${restroom.name}-${restroom.latitude}-${restroom.longitude}`}
                position={{ lat: restroom.latitude, lng: restroom.longitude }}
                icon={getPoopIcon(restroom.rating)}
                onClick={() => handleMarkerClick(restroom)}
              />
            ))}

            {showEmptyAreaPopup && (
              <EmptyAreaPopup
                onDismiss={closeEmptyAreaPopup}
                onAddRestroom={() => {
                  window.location.href = '/auth';
                }}
              />
            )}
          </GoogleMap>
        )}
      </div>

      {/* Review Modal - Shows realistic review on first click */}
      {showReviewModal && selectedRestroom && (
        <DemoReviewModal
          restroom={selectedRestroom}
          onClose={closeReviewModal}
          onSignUp={() => {
            closeReviewModal();
            if (onReviewView) onReviewView();
          }}
        />
      )}
    </div>
  );
}

export default DemoMap;
